/**
 * Dashboard Redux Actions
 * =======================
 * PURPOSE: Redux actions for fetching dashboard data from the backend API
 */

import axios from 'axios';
import {
    DASHBOARD_STATS_REQUEST,
    DASHBOARD_STATS_SUCCESS,
    DASHBOARD_STATS_FAIL,
    DASHBOARD_ADMIN_STATS_REQUEST,
    DASHBOARD_ADMIN_STATS_SUCCESS,
    DASHBOARD_ADMIN_STATS_FAIL,
    DASHBOARD_TUTORIALS_REQUEST,
    DASHBOARD_TUTORIALS_SUCCESS,
    DASHBOARD_TUTORIALS_FAIL,
    DASHBOARD_LIVE_FLOOR_REQUEST,
    DASHBOARD_LIVE_FLOOR_SUCCESS,
    DASHBOARD_LIVE_FLOOR_FAIL,
    DASHBOARD_ACTIVE_SESSIONS_REQUEST,
    DASHBOARD_ACTIVE_SESSIONS_SUCCESS,
    DASHBOARD_ACTIVE_SESSIONS_FAIL,
    DASHBOARD_DEALER_STATUS_REQUEST,
    DASHBOARD_DEALER_STATUS_SUCCESS,
    DASHBOARD_DEALER_STATUS_FAIL,
    DASHBOARD_ALERTS_REQUEST,
    DASHBOARD_ALERTS_SUCCESS,
    DASHBOARD_ALERTS_FAIL,
    DASHBOARD_REVENUE_CHART_REQUEST,
    DASHBOARD_REVENUE_CHART_SUCCESS,
    DASHBOARD_REVENUE_CHART_FAIL,
    DASHBOARD_PLAYER_GROWTH_REQUEST,
    DASHBOARD_PLAYER_GROWTH_SUCCESS,
    DASHBOARD_PLAYER_GROWTH_FAIL,
    DASHBOARD_CLUB_REVENUE_REQUEST,
    DASHBOARD_CLUB_REVENUE_SUCCESS,
    DASHBOARD_CLUB_REVENUE_FAIL,
    DASHBOARD_RECENT_TXN_REQUEST,
    DASHBOARD_RECENT_TXN_SUCCESS,
    DASHBOARD_RECENT_TXN_FAIL,
    DASHBOARD_PENDING_APPROVALS_REQUEST,
    DASHBOARD_PENDING_APPROVALS_SUCCESS,
    DASHBOARD_PENDING_APPROVALS_FAIL,
    DASHBOARD_HIGHLIGHTS_REQUEST,
    DASHBOARD_HIGHLIGHTS_SUCCESS,
    DASHBOARD_HIGHLIGHTS_FAIL,
} from '../constants/dashboardConstants';
import { logout } from './userActions';

const baseUrl = process.env.REACT_APP_API_URL;

// Helper function to get auth token from either adminLogin or userLogin
const getAuthToken = (getState) => {
    const { adminLogin, userLogin } = getState();
    const userInfo = adminLogin?.userInfo || userLogin?.userInfo;
    return userInfo?.token;
};

// Helper function to get auth config
const getAuthConfig = (getState) => {
    const token = getAuthToken(getState);
    return {
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
    };
};

/**
 * Get user dashboard statistics
 * @returns {Function} Redux thunk action
 */
export const getDashboardStats = () => async (dispatch, getState) => {
    try {
        dispatch({ type: DASHBOARD_STATS_REQUEST });

        const config = getAuthConfig(getState);
        const { data } = await axios.get(`${baseUrl}/dashboard/stats`, config);

        dispatch({
            type: DASHBOARD_STATS_SUCCESS,
            payload: data.data,
        });

        return data.data;
    } catch (error) {
        if (error.response?.status === 401) {
            dispatch(logout());
            return;
        }
        dispatch({
            type: DASHBOARD_STATS_FAIL,
            payload:
                error.response && error.response.data.message
                    ? error.response.data.message
                    : error.message,
        });
    }
};

/**
 * Get admin dashboard statistics
 * @returns {Function} Redux thunk action
 */
export const getAdminDashboardStats = () => async (dispatch, getState) => {
    try {
        dispatch({ type: DASHBOARD_ADMIN_STATS_REQUEST });

        const config = getAuthConfig(getState);
        const { data } = await axios.get(`${baseUrl}/dashboard/admin-stats`, config);

        dispatch({
            type: DASHBOARD_ADMIN_STATS_SUCCESS,
            payload: data.data,
        });

        return data.data;
    } catch (error) {
        if (error.response?.status === 401) {
            dispatch(logout());
            return;
        }
        dispatch({
            type: DASHBOARD_ADMIN_STATS_FAIL,
            payload:
                error.response && error.response.data.message
                    ? error.response.data.message
                    : error.message,
        });
    }
};

/**
 * Get poker tutorials
 * @returns {Function} Redux thunk action
 */
export const getPokerTutorials = () => async (dispatch) => {
    try {
        dispatch({ type: DASHBOARD_TUTORIALS_REQUEST });

        const { data } = await axios.get(`${baseUrl}/dashboard/tutorials`);

        dispatch({
            type: DASHBOARD_TUTORIALS_SUCCESS,
            payload: data.data,
        });

        return data.data;
    } catch (error) {
        dispatch({
            type: DASHBOARD_TUTORIALS_FAIL,
            payload:
                error.response && error.response.data.message
                    ? error.response.data.message
                    : error.message,
        });
    }
};

/**
 * Get live floor status (table occupancy)
 * @returns {Function} Redux thunk action
 */
export const getLiveFloorStatus = () => async (dispatch, getState) => {
    try {
        dispatch({ type: DASHBOARD_LIVE_FLOOR_REQUEST });

        const config = getAuthConfig(getState);
        const { data } = await axios.get(`${baseUrl}/dashboard/live-floor`, config);

        dispatch({
            type: DASHBOARD_LIVE_FLOOR_SUCCESS,
            payload: data.data,
        });

        return data.data;
    } catch (error) {
        if (error.response?.status === 401) {
            dispatch(logout());
            return;
        }
        dispatch({
            type: DASHBOARD_LIVE_FLOOR_FAIL,
            payload:
                error.response && error.response.data.message
                    ? error.response.data.message
                    : error.message,
        });
    }
};

/**
 * Get active player sessions
 * @returns {Function} Redux thunk action
 */
export const getActiveSessions = () => async (dispatch, getState) => {
    try {
        dispatch({ type: DASHBOARD_ACTIVE_SESSIONS_REQUEST });

        const config = getAuthConfig(getState);
        const { data } = await axios.get(`${baseUrl}/dashboard/active-sessions`, config);

        dispatch({
            type: DASHBOARD_ACTIVE_SESSIONS_SUCCESS,
            payload: data.data,
        });

        return data.data;
    } catch (error) {
        if (error.response?.status === 401) {
            dispatch(logout());
            return;
        }
        dispatch({
            type: DASHBOARD_ACTIVE_SESSIONS_FAIL,
            payload:
                error.response && error.response.data.message
                    ? error.response.data.message
                    : error.message,
        });
    }
};

/**
 * Get dealer status (active dealer sessions)
 * @returns {Function} Redux thunk action
 */
export const getDealerStatus = () => async (dispatch, getState) => {
    try {
        dispatch({ type: DASHBOARD_DEALER_STATUS_REQUEST });

        const config = getAuthConfig(getState);
        const { data } = await axios.get(`${baseUrl}/dashboard/dealer-status`, config);

        dispatch({
            type: DASHBOARD_DEALER_STATUS_SUCCESS,
            payload: data.data,
        });

        return data.data;
    } catch (error) {
        if (error.response?.status === 401) {
            dispatch(logout());
            return;
        }
        dispatch({
            type: DASHBOARD_DEALER_STATUS_FAIL,
            payload:
                error.response && error.response.data.message
                    ? error.response.data.message
                    : error.message,
        });
    }
};

/**
 * Get dashboard alerts
 * @returns {Function} Redux thunk action
 */
export const getDashboardAlerts = () => async (dispatch, getState) => {
    try {
        dispatch({ type: DASHBOARD_ALERTS_REQUEST });

        const config = getAuthConfig(getState);
        const { data } = await axios.get(`${baseUrl}/dashboard/alerts`, config);

        dispatch({
            type: DASHBOARD_ALERTS_SUCCESS,
            payload: data.data,
        });

        return data.data;
    } catch (error) {
        if (error.response?.status === 401) {
            dispatch(logout());
            return;
        }
        dispatch({
            type: DASHBOARD_ALERTS_FAIL,
            payload:
                error.response && error.response.data.message
                    ? error.response.data.message
                    : error.message,
        });
    }
};

/**
 * Get revenue chart data (last 7 days)
 * @returns {Function} Redux thunk action
 */
export const getRevenueChartData = () => async (dispatch, getState) => {
    try {
        dispatch({ type: DASHBOARD_REVENUE_CHART_REQUEST });

        const config = getAuthConfig(getState);
        const { data } = await axios.get(`${baseUrl}/dashboard/revenue-chart`, config);

        dispatch({
            type: DASHBOARD_REVENUE_CHART_SUCCESS,
            payload: data.data,
        });

        return data.data;
    } catch (error) {
        if (error.response?.status === 401) {
            dispatch(logout());
            return;
        }
        dispatch({
            type: DASHBOARD_REVENUE_CHART_FAIL,
            payload:
                error.response && error.response.data.message
                    ? error.response.data.message
                    : error.message,
        });
    }
};

/**
 * Get player growth data
 * @returns {Function} Redux thunk action
 */
export const getPlayerGrowthData = () => async (dispatch, getState) => {
    try {
        dispatch({ type: DASHBOARD_PLAYER_GROWTH_REQUEST });

        const config = getAuthConfig(getState);
        const { data } = await axios.get(`${baseUrl}/dashboard/player-growth`, config);

        dispatch({
            type: DASHBOARD_PLAYER_GROWTH_SUCCESS,
            payload: data.data,
        });

        return data.data;
    } catch (error) {
        if (error.response?.status === 401) {
            dispatch(logout());
            return;
        }
        dispatch({
            type: DASHBOARD_PLAYER_GROWTH_FAIL,
            payload:
                error.response && error.response.data.message
                    ? error.response.data.message
                    : error.message,
        });
    }
};

/**
 * Get club revenue breakdown
 * @returns {Function} Redux thunk action
 */
export const getClubRevenueData = () => async (dispatch, getState) => {
    try {
        dispatch({ type: DASHBOARD_CLUB_REVENUE_REQUEST });

        const config = getAuthConfig(getState);
        const { data } = await axios.get(`${baseUrl}/dashboard/club-revenue`, config);

        dispatch({
            type: DASHBOARD_CLUB_REVENUE_SUCCESS,
            payload: data.data,
        });

        return data.data;
    } catch (error) {
        if (error.response?.status === 401) {
            dispatch(logout());
            return;
        }
        dispatch({
            type: DASHBOARD_CLUB_REVENUE_FAIL,
            payload:
                error.response && error.response.data.message
                    ? error.response.data.message
                    : error.message,
        });
    }
};

/**
 * Get recent admin transactions
 * @returns {Function} Redux thunk action
 */
export const getRecentTransactions = () => async (dispatch, getState) => {
    try {
        dispatch({ type: DASHBOARD_RECENT_TXN_REQUEST });

        const config = getAuthConfig(getState);
        const { data } = await axios.get(`${baseUrl}/dashboard/recent-transactions`, config);

        dispatch({
            type: DASHBOARD_RECENT_TXN_SUCCESS,
            payload: data.data,
        });

        return data.data;
    } catch (error) {
        if (error.response?.status === 401) {
            dispatch(logout());
            return;
        }
        dispatch({
            type: DASHBOARD_RECENT_TXN_FAIL,
            payload:
                error.response && error.response.data.message
                    ? error.response.data.message
                    : error.message,
        });
    }
};

/**
 * Get pending approvals count
 * @returns {Function} Redux thunk action
 */
export const getPendingApprovals = () => async (dispatch, getState) => {
    try {
        dispatch({ type: DASHBOARD_PENDING_APPROVALS_REQUEST });

        const config = getAuthConfig(getState);
        const { data } = await axios.get(`${baseUrl}/dashboard/pending-approvals`, config);

        dispatch({
            type: DASHBOARD_PENDING_APPROVALS_SUCCESS,
            payload: data.data,
        });

        return data.data;
    } catch (error) {
        if (error.response?.status === 401) {
            dispatch(logout());
            return;
        }
        dispatch({
            type: DASHBOARD_PENDING_APPROVALS_FAIL,
            payload:
                error.response && error.response.data.message
                    ? error.response.data.message
                    : error.message,
        });
    }
};

/**
 * Get today's highlights with comparison to yesterday
 * @returns {Function} Redux thunk action
 */
export const getTodaysHighlights = () => async (dispatch, getState) => {
    try {
        dispatch({ type: DASHBOARD_HIGHLIGHTS_REQUEST });

        const config = getAuthConfig(getState);
        const { data } = await axios.get(`${baseUrl}/dashboard/highlights`, config);

        dispatch({
            type: DASHBOARD_HIGHLIGHTS_SUCCESS,
            payload: data.data,
        });

        return data.data;
    } catch (error) {
        if (error.response?.status === 401) {
            dispatch(logout());
            return;
        }
        dispatch({
            type: DASHBOARD_HIGHLIGHTS_FAIL,
            payload:
                error.response && error.response.data.message
                    ? error.response.data.message
                    : error.message,
        });
    }
};

